import { observer } from 'owa-mobx-react';
import React from 'react';
import FocusedInboxHeader from './FocusedInboxHeader';
import FolderHeaderTab from './FolderHeaderTab';
import MailListHeaderSecondRowTextContent from './MailListHeaderSecondRowTextContent';
import { CopilotResultsTab } from '../../lazyFunctions';
import { isCopilotResultsTabVisible } from 'owa-mail-list-store';
import type { UserSettingsStyleSelector } from '../../utils/getStyleSelectorAsPerUserSettings';
import type { MailboxInfo } from 'owa-client-types';
import type TableView from 'owa-mail-list-store/lib/store/schema/TableView';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { headerTabsContainer } from './MailListHeaderSecondRow.scss';

export interface MailListHeaderTabsProps {
    tableViewId: string;
    containerCssClass: string;
    mailListHeaderStylesAsPerUserSettings?: UserSettingsStyleSelector;

    /** Whether we're in a Focused Inbox scenario (Inbox + Focused Inbox enabled) */
    isInboxWithFocusedPivots: boolean;

    /** Text to display as the folder/header name (used when not in Focused Inbox mode) */
    headerText: string;

    /** Whether to show the favorite toggle star */
    showFavoriteToggle: boolean;
    onFavoriteToggleClick: (evt: React.MouseEvent<unknown>) => void;
    isInFavorites: boolean;

    mailboxInfo: MailboxInfo;
    tableView: TableView | undefined;
}

/**
 * MailListHeaderTabs manages the tab bar in the mail list header's second row.
 *
 * It renders:
 * - The primary content: either FocusedInboxHeader (Focused/Other pivots) or a folder name text
 * - Any additional dynamic tabs (e.g., CopilotResultsTab)
 *
 * This architecture supports adding more tabs in the future (e.g., saved searches, pinned filters)
 * by extending the tab list rendered here rather than scattering tab logic across multiple components.
 */
const MailListHeaderTabs: React.FunctionComponent<MailListHeaderTabsProps> = observer(
    function MailListHeaderTabs(props: MailListHeaderTabsProps) {
        const isCopilotInterplayEnabled = isGlobalFeatureEnabled(
            'mon-copilot-interplay-bizchat-ML'
        );
        const showCopilotResultsTab = isCopilotResultsTabVisible();

        if (!props.isInboxWithFocusedPivots && isCopilotInterplayEnabled) {
            const tabSetSize = showCopilotResultsTab ? 2 : 1;
            return (
                <div className={headerTabsContainer} role="tablist">
                    <FolderHeaderTab
                        text={props.headerText}
                        tableView={props.tableView}
                        posinset={1}
                        setsize={tabSetSize}
                    />
                    {showCopilotResultsTab && (
                        <CopilotResultsTab
                            tableViewId={props.tableViewId}
                            mailboxInfo={props.mailboxInfo}
                            posinset={2}
                            setsize={tabSetSize}
                        />
                    )}
                </div>
            );
        }

        return (
            <>
                {props.isInboxWithFocusedPivots ? (
                    <FocusedInboxHeader
                        tableViewId={props.tableViewId}
                        mailListHeaderStylesAsPerUserSettings={
                            props.mailListHeaderStylesAsPerUserSettings
                        }
                    />
                ) : (
                    <MailListHeaderSecondRowTextContent
                        text={props.headerText}
                        containerCssClass={props.containerCssClass}
                        showFavoriteToggle={props.showFavoriteToggle}
                        onFavoriteToggleClick={props.onFavoriteToggleClick}
                        isInFavorites={props.isInFavorites}
                        styleSelectorAsPerUserSettings={props.mailListHeaderStylesAsPerUserSettings}
                        mailboxInfo={props.mailboxInfo}
                        tableView={props.tableView}
                    />
                )}
                {showCopilotResultsTab && (
                    <CopilotResultsTab
                        tableViewId={props.tableViewId}
                        mailboxInfo={props.mailboxInfo}
                    />
                )}
            </>
        );
    },
    'MailListHeaderTabs'
);

export default MailListHeaderTabs;
